import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { toast } from "~/hooks/use-toast";
import { supplierInvoiceService } from "~/services/purchasing";

import type { ContabilizarInput } from "~/services/purchasing/supplierInvoiceService";
import type {
  SupplierInvoiceFilters,
  SupplierInvoiceImportRow,
  SupplierInvoiceManualInput,
} from "~/types/purchasing";

const QUERY_KEY = "supplier-invoices";

export function useSupplierInvoices(filters?: SupplierInvoiceFilters) {
  return useQuery({
    queryKey: [QUERY_KEY, filters],
    queryFn: () => supplierInvoiceService.list(filters),
  });
}

/** Libro de Compras (SII/F29) for a company + period. */
export function useLibroCompras(companyId: string, from: string, to: string) {
  return useQuery({
    queryKey: [QUERY_KEY, "libro-compras", companyId, from, to],
    queryFn: () => supplierInvoiceService.libroCompras(companyId, from, to),
    enabled: !!companyId && !!from && !!to,
  });
}

/** Confirm-step import: writes the batch for a company, then refreshes invoices + estado de cuenta. */
export function useImportSupplierInvoices() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      rows,
      companyId,
    }: {
      rows: SupplierInvoiceImportRow[];
      companyId: string;
    }) => supplierInvoiceService.createBatch(rows, companyId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: ["supplier-balances"] });
      queryClient.invalidateQueries({ queryKey: ["suppliers"] });
    },
  });
}

/** Manually register a single factura (not from the BSale export), then refresh AP surfaces. */
export function useCreateManualSupplierInvoice() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: SupplierInvoiceManualInput) =>
      supplierInvoiceService.createManual(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: ["supplier-balances"] });
      queryClient.invalidateQueries({ queryKey: ["suppliers"] });
    },
  });
}

/** Reassign a factura to another empresa (before contabilización). */
export function useSetSupplierInvoiceCompany() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      invoiceId,
      companyId,
    }: {
      invoiceId: string;
      companyId: string;
    }) => supplierInvoiceService.setCompany(invoiceId, companyId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
      toast({ title: "Empresa actualizada", description: "La factura se reasignó de empresa." });
    },
    onError: (e: Error) =>
      toast({ title: "Error", description: e.message, variant: "destructive" }),
  });
}

/** Contabilizar: create the draft asiento for a factura, then refresh invoices + accounting. */
export function useContabilizarSupplierInvoice() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: ContabilizarInput) =>
      supplierInvoiceService.contabilizar(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: ["suppliers"] });
      queryClient.invalidateQueries({ queryKey: ["movements"] });
      toast({
        title: "Factura contabilizada",
        description: "Se creó el asiento en borrador. Revísalo y publícalo en Movimientos.",
      });
    },
    onError: (e: Error) =>
      toast({
        title: "No se pudo contabilizar",
        description: e.message,
        variant: "destructive",
      }),
  });
}

/** Attach a manual PDF (uploaded file's public URL or a pasted URL) to a factura. */
export function useAttachSupplierInvoicePdf() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ invoiceId, url }: { invoiceId: string; url: string }) =>
      supplierInvoiceService.attachManualPdf(invoiceId, url),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
      toast({
        title: "PDF adjuntado",
        description: "El documento quedó disponible en la factura.",
      });
    },
    onError: (e: Error) =>
      toast({
        title: "No se pudo adjuntar el PDF",
        description: e.message,
        variant: "destructive",
      }),
  });
}

/** Remove the manually-attached PDF from a factura. */
export function useRemoveSupplierInvoicePdf() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (invoiceId: string) =>
      supplierInvoiceService.removeManualPdf(invoiceId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
      toast({
        title: "Adjunto quitado",
        description: "Se quitó el PDF adjuntado manualmente de la factura.",
      });
    },
    onError: (e: Error) =>
      toast({
        title: "No se pudo quitar el adjunto",
        description: e.message,
        variant: "destructive",
      }),
  });
}
